import { companySetupApi } from "@/apis/custom/companySetup";
import { MainLayout } from "@/components/layouts/main-layout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSession } from "@/lib/auth/auth";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import type React from "react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute('/_authenticated/admin/mandant-erstellen')({
  component: AdminCompanySetup,
})

function AdminCompanySetup() {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState("");
  const [companyDescription, setCompanyDescription] = useState("");
  const [vatId, setVatId] = useState("");
  const [skr, setSkr] = useState<"03" | "04">("03");
  const [userEmails, setUserEmails] = useState("");
  const [accountLength, setAccountLength] = useState(4);
  const [vorsteuerabzug, setVorsteuerabzug] = useState(false);
  const [ocrReview, setOcrReview] = useState(false);
  const [prod, setProd] = useState(false);

  if (isPending) {
    return <div>Loading...</div>;
  }

  if (!session || session?.user?.systemRole !== "superadmin") {
    navigate({ to: "/" });
    return null;
  }

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await companySetupApi.createTenant({
        companyName,
        companyDescription,
        vatId,
        userEmails: userEmails.split(",").map((email) => email.trim()),
        accountLength,
        vorsteuerabzug,
        ocrReview,
        skr,
        prod,
      });
      toast.success("Tenant created successfully");
    } catch (error) {
      toast.error("Failed to create tenant");
    }
  };

  return (
    <MainLayout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Mandant Erstellen</h1>

        <div className="space-y-8 mt-5">
          <form onSubmit={handleCreateTenant} className="space-y-4">
            <h2 className="text-xl font-semibold mb-2">Create New Tenant</h2>
            <div>
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="companyDescription">Company Description</Label>
              <Textarea
                id="companyDescription"
                value={companyDescription}
                onChange={(e) => setCompanyDescription(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="vatId">VAT ID</Label>
              <Input
                id="vatId"
                value={vatId}
                onChange={(e) => setVatId(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="userEmails">User Emails (comma-separated)</Label>
              <Input
                id="userEmails"
                value={userEmails}
                onChange={(e) => setUserEmails(e.target.value)}
                placeholder="user1@example.com, user2@example.com"
              />
            </div>
            <div>
              <Label htmlFor="accountLength">Account Length</Label>
              <Input
                id="accountLength"
                type="number"
                value={accountLength}
                onChange={(e) => setAccountLength(Number(e.target.value))}
                min={1}
              />
            </div>
            <div>
              <Label htmlFor="skr">SKR (Standardkontenrahmen)</Label>
              <Select
                value={skr}
                onValueChange={(value: "03" | "04") => setSkr(value)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select SKR" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="03">03</SelectItem>
                  <SelectItem value="04">04</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="vorsteuerabzug"
                checked={vorsteuerabzug}
                onCheckedChange={(checked) =>
                  setVorsteuerabzug(checked as boolean)
                }
              />
              <Label htmlFor="vorsteuerabzug">Vorsteuerabzug</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="ocrReview"
                checked={ocrReview}
                onCheckedChange={(checked) => setOcrReview(checked as boolean)}
              />
              <Label htmlFor="ocrReview">OCR Review</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="prod"
                checked={prod}
                onCheckedChange={(checked) => setProd(checked as boolean)}
              />
              <Label htmlFor="prod">Production</Label>
            </div>
            <Button type="submit">Create Tenant</Button>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminCompanySetup;
